import { useEffect, useState } from 'react';
import { Alert, Card, Col, Container, Form, Row, Table } from 'react-bootstrap';
import { format } from 'date-fns';

import PageWrapper from 'components/PageWrapper';
import CommonLoader from 'components/CommonLoader';

import AlkoholbevillingImportApi from 'services/KjederegisteretAdminApi/AlkoholbevillingImportApi';
import { AlkoholbevillingDashboardDto } from 'services/KjederegisteretAdminApi/AlkoholbevillingImportApi/types';

import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import useToaster from 'utils/hooks/useToaster';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import { IcebergButton } from '@tradesolution/iceberg-ui-react';

const AlkoholbevillingDashboard = () => {
  const isMountedRef = useIsMountedRef();
  const toaster = useToaster();

  const [days, setDays] = useState<number>(14);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<AlkoholbevillingDashboardDto | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const load = async (daysToLoad: number) => {
    setIsLoading(true);
    try {
      const dto = await AlkoholbevillingImportApi.getDashboard(daysToLoad);
      if (isMountedRef.current) {
        setData(dto);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setData(null);
        toaster.error('Feil ved lasting av dashboard', HttpErrorHelper.formatError(error));
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    void load(days);
  }, [days]);

  const formatDateTime = (date: string | null | undefined) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'dd.MM.yyyy HH:mm');
    } catch {
      return 'Ugyldig dato';
    }
  };

  const downloadExcel = async () => {
    setIsExporting(true);
    try {
      const { blob, fileName } = await AlkoholbevillingImportApi.exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toaster.error('Feil ved eksport til Excel', HttpErrorHelper.formatError(error));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageWrapper isFluid header="Alkoholbevillinger - Import dashboard">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <p className="text-muted" style={{ fontSize: '0.9rem', marginBottom: 0 }}>
              Importjobben henter alle skjenke- og salgstillatelser med gitt type fra
              Helsedirektoratet. Er det match på orgnr sendes bevilling til QA for kobling mot
              enhet. Bevillinger som allerede er koblet til en enhet hoppes over – en bevilling
              kobles derfor kun til én enhet.
            </p>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={3} className="d-flex align-items-end">
            <IcebergButton
              icon="excel-file"
              variant="primary"
              onClick={downloadExcel}
              disabled={isExporting}
            >
              {isExporting ? 'Laster ned…' : 'Last ned alle bevillinger til Excel'}
            </IcebergButton>
          </Col>
        </Row>

        {isLoading && <CommonLoader />}

        {!isLoading && !data && <Alert variant="warning">Dashboard-data ble ikke funnet.</Alert>}

        {!isLoading && data && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Bevillinger i databasen</Card.Title>
                    <Card.Subtitle className="mb-2 text-muted" style={{ fontSize: '0.8rem' }}>
                      Kun skjenke-/salgstillatelser (01SKJE, 02SKIT, 06SKJT, 07SKJB) importert fra
                      Helsedirektoratet
                    </Card.Subtitle>
                    <Table borderless size="sm" style={{ marginBottom: 0 }}>
                      <tbody>
                        <tr>
                          <td style={{ width: '160px' }}>
                            <strong>Totalt</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>{data.totals.totalInDb}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Aktive bevillinger</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>{data.totals.activeTotalInDb}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title>Koblet til enheter via leverandørkundenummer</Card.Title>
                    <Table borderless size="sm" style={{ marginBottom: 0 }}>
                      <tbody>
                        <tr>
                          <td style={{ width: '160px' }}>
                            <strong>Totalt</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {data.totals.connectedToEnhetTotal}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Aktive bevillinger</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {data.totals.activeConnectedToEnhetTotal}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Card>
                  <Card.Body>
                    <Card.Title>Siste kjøring</Card.Title>
                    {!data.lastRun && <div>Ingen registrerte kjøringer.</div>}
                    {data.lastRun && (
                      <Table borderless size="sm" style={{ marginBottom: 0 }}>
                        <tbody>
                          <tr>
                            <td style={{ width: '220px' }}>
                              <strong>Start</strong>
                            </td>
                            <td>{formatDateTime(data.lastRun.startedUtc)}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Status</strong>
                            </td>
                            <td>{data.lastRun.status}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Nye bevillinger fra Helsedirektoratet</strong>
                            </td>
                            <td>{data.lastRun.insertedCount}</td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col>
                <Card>
                  <Card.Body>
                    <Card.Title className="d-flex align-items-center justify-content-between">
                      <span>Siste dager - importer</span>
                      <Form.Group
                        className="d-flex align-items-center gap-2 mb-0"
                        style={{ minWidth: '160px' }}
                      >
                        <Form.Label className="mb-0 text-nowrap" style={{ fontSize: '0.875rem' }}>
                          Antall dager
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          max={90}
                          value={days}
                          onChange={(e) => setDays(parseInt(e.target.value || '14', 10))}
                          style={{ width: '70px' }}
                        />
                      </Form.Group>
                    </Card.Title>
                    <Table hover size="sm">
                      <thead>
                        <tr>
                          <th>Start</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'center' }}>
                            Nye bevillinger fra Helsedirektoratet
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.runs.map((r) => (
                          <tr key={r.id}>
                            <td>{formatDateTime(r.startedUtc)}</td>
                            <td>{r.status}</td>
                            <td style={{ textAlign: 'center' }}>{r.insertedCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </PageWrapper>
  );
};

export default AlkoholbevillingDashboard;
